import {
  OTPError,
  SecretTooShortError,
  SecretTooLongError,
  CounterNegativeError,
  CounterOverflowError,
  CounterNotIntegerError,
  TimeNegativeError,
  TimeNotFiniteError,
  PeriodTooSmallError,
  PeriodTooLargeError,
  TokenLengthError,
  TokenFormatError,
  CounterToleranceError,
  CounterToleranceTooLargeError,
  CounterToleranceNegativeError,
  EpochToleranceError,
  EpochToleranceNegativeError,
  EpochToleranceTooLargeError,
  CryptoPluginMissingError,
  Base32PluginMissingError,
  ConfigurationError,
  SecretMissingError,
  LabelMissingError,
  IssuerMissingError,
  SecretTypeError,
  AlgorithmUnsupportedError,
} from "./errors.js";

import type {
  HashAlgorithm,
  SecretOptions,
  OTPResultOk,
  OTPResultError,
  OTPResult,
} from "./types.js";

/**
 * Singleton TextEncoder instance to avoid repeated allocations
 */
const textEncoder = new TextEncoder();

/**
 * Singleton TextDecoder instance to avoid repeated allocations
 */
const textDecoder = new TextDecoder();

/**
 * Minimum secret length in bytes (128 bits as per RFC 4226)
 */
export const MIN_SECRET_BYTES = 16;

/**
 * Maximum secret length in bytes (512 bits)
 *
 * The 64-byte maximum is not part of the RFCs.
 * This is to prevent excessive memory usage in HMAC operations.
 */
export const MAX_SECRET_BYTES = 64;

/**
 * Recommended secret length in bytes (160 bits as per RFC 4226)
 */
export const RECOMMENDED_SECRET_BYTES = 20;

/**
 * Minimum period in seconds
 */
export const MIN_PERIOD = 1;

/**
 * Maximum period in seconds (1 hour)
 */
export const MAX_PERIOD = 3600;

/**
 * Default period in seconds (30 seconds as per RFC 6238)
 */
export const DEFAULT_PERIOD = 30;

/**
 * Maximum safe integer for counter (2^53 - 1)
 */
export const MAX_COUNTER = Number.MAX_SAFE_INTEGER;

/**
 * Maximum verification window size
 *
 * Limits the number of HMAC computations during verification to prevent DoS attacks.
 * A window of 99 means up to 99 HMAC computations (total checks including current counter).
 * Odd number to cater for equal distribution of time drift + current.
 *
 * For TOTP: window=1 is typically sufficient (allows +-30 seconds clock drift)
 * For HOTP: window=10-50 handles reasonable counter desynchronization
 */
export const MAX_WINDOW = 99;

/**
 * Configurable guardrails for OTP validation
 *
 * Allows overriding default safety limits for non-standard production requirements.
 * Use with caution - custom guardrails can weaken security.
 */
export type OTPGuardrailsConfig = {
  MIN_SECRET_BYTES: number;
  MAX_SECRET_BYTES: number;
  MIN_PERIOD: number;
  MAX_PERIOD: number;
  MAX_COUNTER: number;
  MAX_WINDOW: number;
};

/**
 * Module-private symbol to track guardrail override status
 *
 * This symbol is used as a property key to store whether guardrails contain custom values.
 * Being module-private and a symbol ensures:
 * - Cannot be accessed outside this module (not exported)
 * - Cannot be recreated (each Symbol() call is unique)
 * - Hidden from normal enumeration (Object.keys, JSON.stringify, for-in)
 * - Minimal memory overhead (~1 byte per object)
 * - No garbage collection concerns
 *
 * @internal
 */
const OVERRIDE_SYMBOL = Symbol("otplib.guardrails.override");

/**
 * Complete guardrails configuration
 *
 * This represents the final, immutable configuration used by validation functions.
 * Internally tracks whether any values were overridden from RFC recommendations,
 * enabling security auditing and compliance monitoring without exposing implementation
 * details in the public API.
 *
 * The override status is stored using a module-private Symbol that cannot be accessed
 * or recreated outside this module, providing true encapsulation.
 *
 * @see {@link OTPGuardrailsConfig} for the base configuration structure
 * @see {@link createGuardrails} for creating guardrails instances
 * @see {@link hasGuardrailOverrides} to check if guardrails were customized
 */
export type OTPGuardrails = Readonly<OTPGuardrailsConfig> & {
  [OVERRIDE_SYMBOL]?: boolean;
};

/**
 * Validate guardrail numeric field
 */
function assertGuardrailSafeInteger(
  name: string,
  value: unknown,
  min: number,
): asserts value is number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new ConfigurationError(`Guardrail '${name}' must be a safe integer`);
  }
  if (value < min) {
    throw new ConfigurationError(`Guardrail '${name}' must be >= ${min}`);
  }
}

/**
 * Default guardrails matching RFC recommendations
 *
 * Frozen to ensure immutability. Used as default parameter for validation functions.
 * For custom guardrails, use the createGuardrails() factory function.
 */
const DEFAULT_GUARDRAILS: OTPGuardrails = Object.freeze({
  MIN_SECRET_BYTES,
  MAX_SECRET_BYTES,
  MIN_PERIOD,
  MAX_PERIOD,
  MAX_COUNTER,
  MAX_WINDOW,
  [OVERRIDE_SYMBOL]: false,
});

/**
 * Create guardrails configuration object
 *
 * Factory function that merges custom guardrails with defaults and returns
 * an immutable (frozen) object. Validates custom guardrails to ensure they
 * maintain basic safety invariants.
 *
 * When called without arguments or with `undefined`, returns the default guardrails
 * singleton (optimized to avoid unnecessary allocations). When called with custom
 * values, creates a new frozen object and internally marks it as overridden.
 *
 * @param custom - Optional partial guardrails to override defaults
 * @returns Frozen guardrails object
 * @throws {ConfigurationError} If custom guardrails violate safety invariants
 *
 * @example Basic usage
 * ```ts
 * import { createGuardrails, hasGuardrailOverrides } from '@otplib/core'
 *
 * // Returns default singleton (no overrides)
 * const defaults = createGuardrails();
 * hasGuardrailOverrides(defaults); // false
 *
 * // Creates new object with overrides
 * const custom = createGuardrails({
 *   MIN_SECRET_BYTES: 8,
 *   MAX_WINDOW: 200
 * });
 * hasGuardrailOverrides(custom); // true
 * ```
 *
 * @example Monitoring custom guardrails
 * ```ts
 * import { createGuardrails, hasGuardrailOverrides } from '@otplib/core';
 *
 * const guardrails = createGuardrails({ MAX_WINDOW: 20 });
 *
 * if (hasGuardrailOverrides(guardrails)) {
 *   logger.warn('Non-default guardrails in use', { guardrails });
 * }
 * ```
 *
 * @see {@link hasGuardrailOverrides} to check if guardrails were customized
 */
export function createGuardrails(custom?: Partial<OTPGuardrailsConfig>): OTPGuardrails {
  if (!custom) {
    return DEFAULT_GUARDRAILS;
  }

  if (custom.MIN_SECRET_BYTES !== undefined) {
    assertGuardrailSafeInteger("MIN_SECRET_BYTES", custom.MIN_SECRET_BYTES, 1);
  }

  if (custom.MAX_SECRET_BYTES !== undefined) {
    assertGuardrailSafeInteger("MAX_SECRET_BYTES", custom.MAX_SECRET_BYTES, 1);
  }

  if (custom.MIN_PERIOD !== undefined) {
    assertGuardrailSafeInteger("MIN_PERIOD", custom.MIN_PERIOD, 1);
  }

  if (custom.MAX_PERIOD !== undefined) {
    assertGuardrailSafeInteger("MAX_PERIOD", custom.MAX_PERIOD, 1);
  }

  if (custom.MAX_COUNTER !== undefined) {
    assertGuardrailSafeInteger("MAX_COUNTER", custom.MAX_COUNTER, 0);
  }

  if (custom.MAX_WINDOW !== undefined) {
    assertGuardrailSafeInteger("MAX_WINDOW", custom.MAX_WINDOW, 1);
  }

  const merged = {
    ...DEFAULT_GUARDRAILS,
    ...custom,
  };

  if (merged.MIN_SECRET_BYTES > merged.MAX_SECRET_BYTES) {
    throw new ConfigurationError("Guardrail 'MIN_SECRET_BYTES' must be <= 'MAX_SECRET_BYTES'");
  }

  if (merged.MIN_PERIOD > merged.MAX_PERIOD) {
    throw new ConfigurationError("Guardrail 'MIN_PERIOD' must be <= 'MAX_PERIOD'");
  }

  return Object.freeze({
    ...merged,
    [OVERRIDE_SYMBOL]: true,
  });
}

/**
 * Check if guardrails contain custom overrides
 *
 * Returns `true` if the guardrails object was created with custom values,
 * `false` if using RFC-recommended defaults. Useful for security auditing,
 * compliance monitoring, and development warnings.
 *
 * This function accesses a module-private Symbol property that cannot be
 * accessed or modified outside this module, ensuring reliable detection.
 *
 * @param guardrails - The guardrails object to check
 * @returns `true` if guardrails were customized, `false` if using defaults
 *
 * @example Security monitoring
 * ```ts
 * import { createGuardrails, hasGuardrailOverrides } from '@otplib/core';
 *
 * const guardrails = createGuardrails({ MAX_WINDOW: 20 });
 *
 * if (hasGuardrailOverrides(guardrails)) {
 *   console.warn('Custom guardrails detected:', guardrails);
 *   // Log to security audit system
 * }
 * ```
 *
 * @example Compliance check
 * ```ts
 * function validateGuardrails(guardrails: OTPGuardrails) {
 *   if (hasGuardrailOverrides(guardrails)) {
 *     throw new Error('Custom guardrails not allowed in production');
 *   }
 * }
 * ```
 */
export function hasGuardrailOverrides(guardrails: OTPGuardrails): boolean {
  return guardrails[OVERRIDE_SYMBOL] ?? false;
}

/**
 * Every accepted name for each supported algorithm, grouped by the canonical
 * name it resolves to
 *
 * Each list includes the canonical name itself, so the lookup built from this
 * needs no separate case for it. (Strictly, a registry such as IANA's lists the
 * canonical `Name` apart from its `Alias` entries; folding them together here
 * keeps the lookup a single branch.)
 *
 * Enumerated rather than derived from a pattern. A pattern is a compressed
 * encoding of this list that can generalize past its intent - an earlier
 * separator-stripping implementation accepted `'sha-2-5-6'` and `'s-h-a-1'`
 * for exactly that reason. Nothing here can drift: the accepted set is the
 * list.
 *
 * The `satisfies` constraint is load-bearing. It requires an entry for every
 * member of {@link HashAlgorithm} and forbids keys outside it, so widening the
 * union fails to compile until aliases are supplied here.
 *
 * @internal
 */
const HASH_ALGORITHM_ALIASES = {
  sha1: ["sha1", "sha-1", "sha_1"],
  sha256: ["sha256", "sha-256", "sha_256"],
  sha512: ["sha512", "sha-512", "sha_512"],
} as const satisfies Record<HashAlgorithm, readonly string[]>;

/**
 * The hash algorithms supported for HMAC operations
 *
 * Derived from `HASH_ALGORITHM_ALIASES` rather than written out again, so
 * the runtime allowlist cannot disagree with the names that resolve to it. That
 * matters because this array is both the default `supported` set and the
 * intersection base in `normalizeHashAlgorithm`: an algorithm missing here is
 * rejected at runtime no matter what the types say.
 *
 * Frozen because it is a runtime allowlist, not merely a type-level constant -
 * types are erased at compile time and would leave the exported array mutable,
 * allowing in-process code to corrupt the validation policy and its reported
 * supported set globally.
 */
export const HASH_ALGORITHMS = Object.freeze(
  Object.keys(HASH_ALGORITHM_ALIASES) as (keyof typeof HASH_ALGORITHM_ALIASES)[],
);

/**
 * Lowercased name to canonical algorithm
 *
 * A `Map` rather than an object literal: `ALIAS_LOOKUP.get('__proto__')` is
 * `undefined`, whereas an object would resolve `'__proto__'`, `'constructor'`
 * and `'toString'` through the prototype chain and hand back inherited junk.
 * That safety holds by construction, with no `Object.hasOwn` guard to forget.
 *
 * @internal
 */
const ALIAS_LOOKUP: ReadonlyMap<string, HashAlgorithm> = new Map(
  Object.entries(HASH_ALGORITHM_ALIASES).flatMap(([canonical, aliases]) =>
    aliases.map((alias) => [alias, canonical as HashAlgorithm] as const),
  ),
);

/**
 * Options for {@link normalizeHashAlgorithm}
 */
export type NormalizeHashAlgorithmOptions = {
  /**
   * Accepted algorithms
   *
   * **Omitted and empty mean different things, deliberately.** Omitting it (or
   * passing `undefined`) accepts all of {@link HASH_ALGORITHMS} - the
   * backward-compatible reading for a `CryptoPlugin` written before
   * `algorithms` existed, which supports the full set by definition. An
   * explicit `[]` declares support for nothing and rejects every algorithm; it
   * must never fall back to the full set, or a plugin could not express that.
   *
   * Intersected with {@link HASH_ALGORITHMS}, so this can only ever narrow the
   * allowlist - a crypto plugin cannot widen the library's supported set.
   */
  supported?: readonly HashAlgorithm[];

  /**
   * Name of the calling crypto plugin, used to enrich the error message
   */
  plugin?: string;
};

/**
 * Normalize and validate a hash algorithm
 *
 * Matching ignores case and accepts an optional `-` or `_` before the digest
 * size, so `'SHA1'`, `'Sha1'`, `'SHA-1'` and `'sha_1'` all resolve to `'sha1'`.
 * This is safe to be forgiving about: every accepted alias names the *same*
 * algorithm. No alias of any other digest appears in the table, so `'sha3-256'`
 * and `'sha-384'` are rejected outright.
 *
 * Anything that is not an alias of a supported algorithm is rejected rather
 * than guessed at, because substituting a *different* algorithm produces
 * self-consistent tokens that do not match conforming implementations using the
 * requested algorithm - a failure that can remain hidden until interoperability
 * is tested.
 *
 * @param value - The algorithm to normalize
 * @param options - Narrowed algorithm set and calling plugin name
 * @returns The canonical lowercase algorithm
 * @throws {AlgorithmUnsupportedError} If the value is not a supported algorithm
 *
 * @example
 * ```typescript
 * normalizeHashAlgorithm('SHA1');   // 'sha1'
 * normalizeHashAlgorithm('SHA-1');  // 'sha1'
 * normalizeHashAlgorithm('sha384'); // throws AlgorithmUnsupportedError
 *
 * // A plugin backed by a restricted implementation
 * normalizeHashAlgorithm('sha256', { supported: ['sha1'], plugin: 'custom' });
 * ```
 */
export function normalizeHashAlgorithm(
  value: unknown,
  options: NormalizeHashAlgorithmOptions = {},
): HashAlgorithm {
  // Tested against `undefined` rather than truthiness, because `[]` must not
  // take the same branch as an omitted value: omitted means "the full set"
  // (a plugin predating `algorithms` supports all of them), while `[]` means
  // "none" and has to reject everything. Simplifying this to a `.length` check
  // would silently turn an empty declaration back into the full set.
  //
  // The intersection is applied rather than trusting the input: `supported`
  // reaches here from crypto plugins, including untyped ones, so it may only
  // ever narrow.
  const { supported: declared, plugin } = options;
  const supported =
    declared === undefined
      ? HASH_ALGORITHMS
      : HASH_ALGORITHMS.filter((algorithm) => declared.includes(algorithm));

  if (typeof value === "string") {
    const canonical = ALIAS_LOOKUP.get(value.toLowerCase());
    if (canonical !== undefined && supported.includes(canonical)) {
      return canonical;
    }
  }

  throw new AlgorithmUnsupportedError(value, { supported, plugin });
}

/**
 * Validate secret key
 *
 * @param secret - The secret to validate
 * @param guardrails - Validation guardrails (defaults to RFC recommendations)
 * @throws {SecretTooShortError} If secret is too short
 * @throws {SecretTooLongError} If secret is too long
 */
export function validateSecret(
  secret: Uint8Array,
  guardrails: OTPGuardrails = DEFAULT_GUARDRAILS,
): void {
  if (secret.length < guardrails.MIN_SECRET_BYTES) {
    throw new SecretTooShortError(guardrails.MIN_SECRET_BYTES, secret.length);
  }

  if (secret.length > guardrails.MAX_SECRET_BYTES) {
    throw new SecretTooLongError(guardrails.MAX_SECRET_BYTES, secret.length);
  }
}

/**
 * Validate counter value
 *
 * @param counter - The counter to validate
 * @param guardrails - Validation guardrails (defaults to RFC recommendations)
 * @throws {CounterNegativeError} If counter is negative
 * @throws {CounterOverflowError} If counter exceeds safe integer
 */
export function validateCounter(
  counter: number | bigint,
  guardrails: OTPGuardrails = DEFAULT_GUARDRAILS,
): void {
  if (typeof counter === "number") {
    if (!Number.isFinite(counter) || !Number.isInteger(counter)) {
      throw new CounterNotIntegerError();
    }

    if (!Number.isSafeInteger(counter)) {
      // Numbers outside the safe integer range cannot be reliably converted to bigint
      throw new CounterOverflowError();
    }
  }

  const value = typeof counter === "bigint" ? counter : BigInt(counter);

  if (value < 0n) {
    throw new CounterNegativeError();
  }

  if (value > BigInt(guardrails.MAX_COUNTER)) {
    throw new CounterOverflowError();
  }
}

/**
 * Validate time value
 *
 * @param time - The time in seconds to validate
 * @throws {TimeNegativeError} If time is negative
 */
export function validateTime(time: number): void {
  if (!Number.isFinite(time)) {
    throw new TimeNotFiniteError();
  }

  if (time < 0) {
    throw new TimeNegativeError();
  }
}

/**
 * Validate period value
 *
 * @param period - The period in seconds to validate
 * @param guardrails - Validation guardrails (defaults to RFC recommendations)
 * @throws {PeriodTooSmallError} If period is too small
 * @throws {PeriodTooLargeError} If period is too large
 */
export function validatePeriod(
  period: number,
  guardrails: OTPGuardrails = DEFAULT_GUARDRAILS,
): void {
  if (!Number.isInteger(period) || period < guardrails.MIN_PERIOD) {
    throw new PeriodTooSmallError(guardrails.MIN_PERIOD);
  }

  if (period > guardrails.MAX_PERIOD) {
    throw new PeriodTooLargeError(guardrails.MAX_PERIOD);
  }
}

/**
 * Validate token
 *
 * @param token - The token string to validate
 * @param digits - Expected number of digits
 * @throws {TokenLengthError} If token has incorrect length
 * @throws {TokenFormatError} If token contains non-digit characters
 */
export function validateToken(token: string, digits: number): void {
  if (token.length !== digits) {
    throw new TokenLengthError(digits, token.length);
  }

  if (!/^\d+$/.test(token)) {
    throw new TokenFormatError();
  }
}

/**
 * Validate counter tolerance for HOTP verification
 *
 * Prevents DoS attacks by limiting the number of counter values checked.
 *
 * @param counterTolerance - Counter tolerance specification (number or array of offsets)
 * @param guardrails - Validation guardrails (defaults to RFC recommendations)
 * @throws {CounterToleranceTooLargeError} If tolerance size exceeds MAX_WINDOW
 *
 * @example
 * ```ts
 * validateCounterTolerance(1);        // OK: [0, 1] = 2 checks
 * validateCounterTolerance(98);       // OK: [0, 98] = 99 checks
 * validateCounterTolerance(99);       // Throws: exceeds MAX_WINDOW
 * validateCounterTolerance([0, 1]);   // OK: 2 offsets
 * ```
 */
export function validateCounterTolerance(
  counterTolerance: number | [number, number],
  guardrails: OTPGuardrails = DEFAULT_GUARDRAILS,
): void {
  const [past, future] = normalizeCounterTolerance(counterTolerance);

  if (!Number.isSafeInteger(past) || !Number.isSafeInteger(future)) {
    throw new CounterToleranceError("Counter tolerance values must be safe integers");
  }

  if (past < 0 || future < 0) {
    throw new CounterToleranceNegativeError();
  }

  const totalChecks = past + future + 1;

  if (totalChecks > guardrails.MAX_WINDOW) {
    throw new CounterToleranceTooLargeError(guardrails.MAX_WINDOW, totalChecks);
  }
}

/**
 * Validate epoch tolerance for TOTP verification
 *
 * Prevents DoS attacks by limiting the time range checked.
 * Also validates that tolerance values are non-negative.
 *
 * @param epochTolerance - Epoch tolerance specification (number or tuple [past, future])
 * @param period - The TOTP period in seconds (default: 30). Used to calculate max tolerance.
 * @param guardrails - Validation guardrails (defaults to RFC recommendations)
 * @throws {EpochToleranceNegativeError} If tolerance contains negative values
 * @throws {EpochToleranceTooLargeError} If tolerance exceeds MAX_WINDOW periods
 *
 * @example
 * ```ts
 * validateEpochTolerance(30);            // OK: 30 seconds symmetric
 * validateEpochTolerance([5, 0]);        // OK: 5 seconds past only
 * validateEpochTolerance([-5, 0]);       // Throws: negative values not allowed
 * validateEpochTolerance(1471);          // Throws with default guardrails (30s period)
 * validateEpochTolerance(2940, 60);      // OK with 60s period
 * ```
 */
export function validateEpochTolerance(
  epochTolerance: number | [number, number],
  period: number = DEFAULT_PERIOD,
  guardrails: OTPGuardrails = DEFAULT_GUARDRAILS,
): void {
  const [pastTolerance, futureTolerance] = Array.isArray(epochTolerance)
    ? epochTolerance
    : [epochTolerance, epochTolerance];

  if (!Number.isSafeInteger(pastTolerance) || !Number.isSafeInteger(futureTolerance)) {
    throw new EpochToleranceError("Epoch tolerance values must be safe integers");
  }

  // Check for negative values
  if (pastTolerance < 0 || futureTolerance < 0) {
    throw new EpochToleranceNegativeError();
  }

  // MAX_WINDOW checks means at most MAX_WINDOW - 1 periods of tolerance
  // (the current time step always consumes one check).
  const maxToleranceSeconds = (guardrails.MAX_WINDOW - 1) * period;
  const totalToleranceSeconds = pastTolerance + futureTolerance;

  if (totalToleranceSeconds > maxToleranceSeconds) {
    throw new EpochToleranceTooLargeError(maxToleranceSeconds, totalToleranceSeconds);
  }
}

/**
 * Convert counter to 8-byte big-endian array
 *
 * Per RFC 4226 Section 5.1, the counter value is represented as an 8-byte
 * big-endian (network byte order) unsigned integer.
 *
 * @see {@link https://tools.ietf.org/html/rfc4226#section-5.1 | RFC 4226 Section 5.1 - Symbol Descriptions}
 *
 * @param value - The counter value to convert
 * @returns 8-byte big-endian array
 */
export function counterToBytes(value: number | bigint): Uint8Array {
  const bigintValue = typeof value === "bigint" ? value : BigInt(value);
  const buffer = new ArrayBuffer(8);
  const view = new DataView(buffer);

  view.setBigUint64(0, bigintValue, false);

  return new Uint8Array(buffer);
}

/**
 * Perform Dynamic Truncation as per RFC 4226 Section 5.3
 *
 * The algorithm:
 * 1. Take the low-order 4 bits of the last byte as offset
 * 2. Extract 4 bytes starting at offset
 * 3. Mask the most significant bit to get a 31-bit unsigned integer
 *
 * This ensures consistent extraction across different HMAC output sizes
 * while producing a value that fits in a signed 32-bit integer.
 *
 * @see {@link https://tools.ietf.org/html/rfc4226#section-5.3 | RFC 4226 Section 5.3 - Generating an HOTP Value}
 *
 * @param hmacResult - HMAC result (at least 20 bytes for SHA-1)
 * @returns Truncated 31-bit unsigned integer
 */
export function dynamicTruncate(hmacResult: Uint8Array): number {
  const offset = hmacResult[hmacResult.length - 1] & 0x0f;

  const binary =
    ((hmacResult[offset] & 0x7f) << 24) |
    (hmacResult[offset + 1] << 16) |
    (hmacResult[offset + 2] << 8) |
    hmacResult[offset + 3];

  return binary;
}

/**
 * Convert truncated integer to OTP string with specified digits
 *
 * Computes: Snum mod 10^Digit (RFC 4226 Section 5.3)
 *
 * The result is zero-padded to ensure consistent length,
 * as required for proper token comparison.
 *
 * @see {@link https://tools.ietf.org/html/rfc4226#section-5.3 | RFC 4226 Section 5.3 - Generating an HOTP Value}
 *
 * @param value - The truncated integer value (Snum)
 * @param digits - Number of digits for the OTP (Digit, typically 6-8)
 * @returns OTP string with leading zeros if necessary
 */
export function truncateDigits(value: number, digits: number): string {
  const maxOtp = 10 ** digits;
  const otp = value % maxOtp;
  return otp.toString().padStart(digits, "0");
}

/**
 * Validate that two byte arrays have equal length
 *
 * Useful as a preliminary check before performing byte-by-byte comparisons.
 *
 * @param a - First byte array
 * @param b - Second byte array
 * @returns true if arrays have equal length, false otherwise
 */
export function validateByteLengthEqual(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length;
}

/**
 * Constant-time comparison to prevent timing attacks
 *
 * This implements a timing-safe equality check as recommended in
 * RFC 4226 Section 7.2 for token validation to prevent
 * timing side-channel attacks.
 *
 * @see {@link https://tools.ietf.org/html/rfc4226#section-7.2 | RFC 4226 Section 7.2 - Validation and Verification}
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if values are equal, false otherwise
 */
export function constantTimeEqual(a: string | Uint8Array, b: string | Uint8Array): boolean {
  const bufA = stringToBytes(a);
  const bufB = stringToBytes(b);

  if (!validateByteLengthEqual(bufA, bufB)) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < bufA.length; i++) {
    result |= bufA[i] ^ bufB[i];
  }

  return result === 0;
}

/**
 * Get HMAC digest size in bytes for a given algorithm
 *
 * @param algorithm - The hash algorithm
 * @returns Digest size in bytes
 * @throws {AlgorithmUnsupportedError} If the algorithm is not supported
 */
export function getDigestSize(algorithm: HashAlgorithm): number {
  switch (algorithm) {
    case "sha1":
      return 20;
    case "sha256":
      return 32;
    case "sha512":
      return 64;
    default:
      throw new AlgorithmUnsupportedError(algorithm, { supported: HASH_ALGORITHMS });
  }
}

/**
 * Convert a string or Uint8Array to Uint8Array
 *
 * This utility function normalizes input to Uint8Array, converting strings
 * using UTF-8 encoding. Uint8Array inputs are returned as-is.
 *
 * Use this to convert raw secret strings (passphrases) to Uint8Array
 * before passing them to generation or verification functions.
 *
 * @param value - The value to convert (string or Uint8Array)
 * @returns The value as a Uint8Array (UTF-8 encoded for strings)
 *
 * @example
 * ```ts
 * import { stringToBytes } from '@otplib/core'
 *
 * const bytes1 = stringToBytes('1234567890123456')
 * // Returns: Uint8Array([49, 50, 51, 52, 53, 54, 55, 56, 57, 48, 49, 50, 51, 52, 53, 54])
 *
 * const bytes2 = stringToBytes(new Uint8Array([1, 2, 3]))
 * // Returns: Uint8Array([1, 2, 3]) - returned as-is
 * ```
 */
export function stringToBytes(value: string | Uint8Array): Uint8Array {
  return typeof value === "string" ? textEncoder.encode(value) : value;
}

/**
 * Convert bytes to UTF-8 string
 *
 * Uses TextDecoder for proper UTF-8 handling.
 *
 * @param bytes - Uint8Array to convert
 * @returns UTF-8 string
 *
 * @example
 * ```ts
 * const str = bytesToString(new Uint8Array([104, 101, 108, 108, 111]));
 * // str === "hello"
 * ```
 */
export function bytesToString(bytes: Uint8Array): string {
  return textDecoder.decode(bytes);
}

/**
 * Normalize secret input to Uint8Array
 *
 * Accepts either a Base32-encoded string or Uint8Array and returns Uint8Array.
 * If a Base32Plugin is provided, string secrets will be automatically decoded.
 *
 * **Note**: By default, strings are assumed to be Base32 encoded.
 * If you have a raw string secret (e.g. a passphrase), you must convert it
 * to a Uint8Array using {@link stringToBytes} before calling this function.
 *
 * @param secret - The secret to normalize (Base32 string or Uint8Array)
 * @param base32 - Optional Base32Plugin to decode string secrets
 * @returns The secret as Uint8Array
 * @throws {Error} If secret is a string but no Base32Plugin is provided
 *
 * @example
 * ```ts
 * import { normalizeSecret } from '@otplib/core'
 * import { ScureBase32Plugin } from '@otplib/plugin-base32-scure'
 *
 * const base32 = new ScureBase32Plugin()
 *
 * // Uint8Array - returned as-is
 * const secret1 = normalizeSecret(new Uint8Array([1, 2, 3]))
 *
 * // Base32 string - automatically decoded
 * const secret2 = normalizeSecret('JBSWY3DPEHPK3PXP', base32)
 * ```
 */
export function normalizeSecret(
  secret: string | Uint8Array,
  base32?: { decode: (str: string) => Uint8Array },
): Uint8Array {
  if (typeof secret === "string") {
    requireBase32Plugin(base32);
    return base32.decode(secret);
  }
  return secret;
}

/**
 * Generate a random Base32-encoded secret
 *
 * Creates a cryptographically secure random secret suitable for OTP generation.
 * The default length of 20 bytes (160 bits) matches RFC 4226 recommendations
 * and provides good security margin.
 *
 * @param options - Secret generation options
 * @returns Base32-encoded secret string (without padding for Google Authenticator compatibility)
 *
 * @example
 * ```ts
 * import { generateSecret } from '@otplib/core';
 * import { NodeCryptoPlugin } from '@otplib/plugin-crypto-node';
 * import { ScureBase32Plugin } from '@otplib/plugin-base32-scure';
 *
 * const secret = generateSecret({
 *   crypto: new NodeCryptoPlugin(),
 *   base32: new ScureBase32Plugin(),
 * });
 * // Returns: 'JBSWY3DPEHPK3PXP...' (32 characters)
 * ```
 *
 * @example Custom length
 * ```ts
 * const secret = generateSecret({
 *   crypto: new NodeCryptoPlugin(),
 *   base32: new ScureBase32Plugin(),
 *   length: 32, // 256 bits for SHA-256
 * });
 * ```
 */
export function generateSecret(options: SecretOptions): string {
  const { crypto, base32, length = RECOMMENDED_SECRET_BYTES } = options;

  requireCryptoPlugin(crypto);
  requireBase32Plugin(base32);

  const randomBytes = crypto.randomBytes(length);
  return base32.encode(randomBytes, { padding: false });
}

/**
 * Normalize counter tolerance to [past, future] tuple
 *
 * Converts a number or tuple counter tolerance specification into a [past, future] tuple
 * - Number: creates look-ahead only tolerance [0, tolerance] (default for security)
 * - Tuple: uses the tuple as-is (past, future)
 *
 * The default behavior (number → look-ahead only) improves security by preventing
 * replay attacks. HOTP counters should only move forward in normal operation.
 *
 * @param counterTolerance - Counter tolerance specification (number or tuple [past, future])
 * @returns Tuple [past, future] representing counters to check
 *
 * @example
 * ```ts
 * normalizeCounterTolerance(0)        // [0, 0]
 * normalizeCounterTolerance(5)        // [0, 5] - look-ahead only (secure default)
 * normalizeCounterTolerance([10, 5])  // [10, 5] - explicit past/future
 * normalizeCounterTolerance([5, 5])   // [5, 5] - explicit symmetric (use with caution)
 * ```
 */
export function normalizeCounterTolerance(
  counterTolerance: number | [number, number] = 0,
): [number, number] {
  return Array.isArray(counterTolerance) ? counterTolerance : [0, counterTolerance];
}

/**
 * Normalize epoch tolerance to [past, future] tuple
 *
 * Converts a number or tuple epoch tolerance specification into a [past, future] tuple
 * - Number: creates symmetric tolerance [tolerance, tolerance]
 * - Tuple: uses the tuple as-is
 *
 * @param epochTolerance - Epoch tolerance specification (number or tuple [past, future])
 * @returns Tuple [pastTolerance, futureTolerance] in seconds
 *
 * @example
 * ```ts
 * normalizeEpochTolerance(0)        // [0, 0]
 * normalizeEpochTolerance(30)       // [30, 30]
 * normalizeEpochTolerance([5, 0])   // [5, 0]
 * normalizeEpochTolerance([10, 5])  // [10, 5]
 * ```
 */
export function normalizeEpochTolerance(
  epochTolerance: number | [number, number] = 0,
): [number, number] {
  return Array.isArray(epochTolerance) ? epochTolerance : [epochTolerance, epochTolerance];
}

/**
 * Require crypto plugin to be configured
 *
 * @param crypto - The crypto plugin
 * @throws {CryptoPluginMissingError} If crypto plugin is not set
 */
export function requireCryptoPlugin<T>(crypto: T | undefined): asserts crypto is T {
  if (!crypto) {
    throw new CryptoPluginMissingError();
  }
}

/**
 * Require Base32 plugin to be configured
 *
 * @param base32 - The Base32 plugin
 * @throws {Base32PluginMissingError} If Base32 plugin is not set
 */
export function requireBase32Plugin<T>(base32: T | undefined): asserts base32 is T {
  if (!base32) {
    throw new Base32PluginMissingError();
  }
}

/**
 * Require secret to be configured
 *
 * @param secret - The secret value
 * @throws {SecretMissingError} If secret is not set
 */
export function requireSecret<T>(secret: T | undefined): asserts secret is T {
  if (!secret) {
    throw new SecretMissingError();
  }
}

/**
 * Require label to be configured (for URI generation)
 *
 * @param label - The label value
 * @throws {LabelMissingError} If label is not set
 */
export function requireLabel(label: string | undefined): asserts label is string {
  if (!label) {
    throw new LabelMissingError();
  }
}

/**
 * Require issuer to be configured (for URI generation)
 *
 * @param issuer - The issuer value
 * @throws {IssuerMissingError} If issuer is not set
 */
export function requireIssuer(issuer: string | undefined): asserts issuer is string {
  if (!issuer) {
    throw new IssuerMissingError();
  }
}

/**
 * Require secret to be a Base32 string (for URI generation)
 *
 * @param secret - The secret value
 * @throws {SecretTypeError} If secret is not a string
 */
export function requireBase32String(secret: string | Uint8Array): asserts secret is string {
  if (typeof secret !== "string") {
    throw new SecretTypeError();
  }
}

/**
 * Create a success result
 * @internal
 */
function ok<T>(value: T): OTPResultOk<T> {
  return { ok: true, value };
}

/**
 * Create a failure result
 * @internal
 */
function err<E>(error: E): OTPResultError<E> {
  return { ok: false, error };
}

/**
 * Wrap a synchronous function to return OTPResult instead of throwing
 *
 * Preserves the original OTPError subclass so users can access
 * specific error information via instanceof checks.
 *
 * @internal
 */
export function wrapResult<T, Args extends unknown[]>(
  fn: (...args: Args) => T,
): (...args: Args) => OTPResult<T, OTPError> {
  return (...args: Args): OTPResult<T, OTPError> => {
    try {
      return ok(fn(...args));
    } catch (error) {
      return err(error as OTPError);
    }
  };
}

/**
 * Wrap an async function to return OTPResult instead of throwing
 *
 * Preserves the original OTPError subclass so users can access
 * specific error information via instanceof checks.
 *
 * @internal
 */
export function wrapResultAsync<T, Args extends unknown[]>(
  fn: (...args: Args) => Promise<T>,
): (...args: Args) => Promise<OTPResult<T, OTPError>> {
  return async (...args: Args): Promise<OTPResult<T, OTPError>> => {
    try {
      return ok(await fn(...args));
    } catch (error) {
      return err(error as OTPError);
    }
  };
}
